import {useTranslation} from '@aboutyou/i18n';
import React, {FC, useRef, useState} from 'react';
import styled from 'styled-components';
import {useOnClickOutside} from '../../../../../hooks/useOnClickOutside/useOnClickOutside';
import {Divider} from '../../../../atoms/Divider/Divider';
import {Grid} from '../../../../atoms/Grid/Grid';
import {FilterByFavoriteBrands} from '../../FilterContent/Brand/FilterByFavoriteBrands';
import {HighlightedBrands} from '../../FilterContent/Brand/HighlightedBrands';
import {SelectedBrands} from '../../FilterContent/Brand/SelectedBrands';
import {
  HighlightBrand,
  HighlightedBrandType,
} from '../../FilterContent/Brand/types';
import {GenericFilteredItems} from '../../FilterContent/Shared/GenericFilteredItems/GenericFilteredItems';
import {GenericFilterItemGroup} from '../../FilterContent/Shared/GenericFilteredItems/types';
import {GenericFilterSearchField} from '../../FilterContent/Shared/GenericSearchField';
import {FilterDropdown} from '../FilterDropdown';

interface Props {
  activeBrandIds: Array<string>;
  excludedHighlightedBrands?: Array<string>;
  favoriteBrandsActive?: boolean;
  brandGroups: Array<GenericFilterItemGroup>;
  highlightedBrands?: Array<HighlightBrand>;
  highlightedBrandsType?: HighlightedBrandType;
  label: string;
  onBrandClick: (itemId: string, itemSlug?: string) => void;
  onHighlightedBrandClick: (brandId: string, brandSlug?: string) => void;
  highlightedBrandsLabel: string;
  onFavoriteBrandsToggle: () => void;
  onFilterReset: () => void;
  onOpen?: () => void;
  onClose?: () => void;
}

export const FilterDropdownBrand: FC<Props> = ({
  activeBrandIds,
  brandGroups,
  excludedHighlightedBrands,
  favoriteBrandsActive,
  highlightedBrands,
  highlightedBrandsLabel,
  highlightedBrandsType,
  label,
  onBrandClick,
  onHighlightedBrandClick,
  onFavoriteBrandsToggle,
  onFilterReset,
  onOpen,
  onClose,
}) => {
  const {t} = useTranslation();

  const [search, setSearch] = useState('');

  const dropDownBodyRef = useRef<HTMLDivElement>(null);

  const [isFocusInside, setIsFocusInside] = useState(false);
  useOnClickOutside(dropDownBodyRef, () => setIsFocusInside(false));
  const onCloseBrandFilter = () => {
    setSearch('');
    setIsFocusInside(false);
    onClose && onClose();
  };

  return (
    <FilterDropdown
      dataTestId="filterDropdownBrand"
      activeCount={activeBrandIds.length}
      label={label}
      onClose={onCloseBrandFilter}
      onReset={onFilterReset}
      onOpen={onOpen}
      dropDownBodyRef={dropDownBodyRef}
      filterContent={
        <div>
          <GenericFilterSearchField
            search={search}
            setSearch={setSearch}
            onFocus={() => setIsFocusInside(true)}
            searchPlaceholder={t('Search for brands')}
          />
          <SelectedBrands
            search={search}
            activeBrandIds={activeBrandIds}
            brandGroups={brandGroups}
            onBrandClick={onBrandClick}
          />
          {isFocusInside && (
            <GenericFilteredItems
              search={search}
              activeIds={activeBrandIds}
              itemGroups={brandGroups}
              onItemClick={onBrandClick}
              dataTestId="BrandSearch"
              enableFilterFlyout={false} // desktop version needs different styling
            />
          )}
        </div>
      }
      filterFooterContent={
        <>
          {Boolean(!isFocusInside && highlightedBrands?.length) && (
            <>
              <StyledHighlightedBrands
                dataTestId="highlightedBrands"
                brands={highlightedBrands!}
                label={highlightedBrandsLabel}
                onBrandClick={onHighlightedBrandClick}
                excludedHighlightedBrands={excludedHighlightedBrands}
                activeBrandIds={activeBrandIds}
                highlightedBrandsType={highlightedBrandsType}
                favoriteBrandsActive={Boolean(favoriteBrandsActive)}
                Wrapper={HighlightedBrandsWrapper}
              />
              {highlightedBrandsType === 'personalizedBrands' && (
                <>
                  <Divider />
                  <StyledFilterByFavoriteBrands
                    favoriteBrandsActive={Boolean(favoriteBrandsActive)}
                    testId="filterFlyoutSectionBrandFavoriteBrandsToggle"
                    onFavoriteBrandsToggle={onFavoriteBrandsToggle}
                  />
                </>
              )}
            </>
          )}
        </>
      }
    />
  );
};

const StyledHighlightedBrands = styled(HighlightedBrands)`
  padding: 0 24px;
`;

const StyledFilterByFavoriteBrands = styled(FilterByFavoriteBrands)`
  padding: 0 24px 24px 24px;
  box-sizing: border-box;
`;

const HighlightedBrandsWrapper = styled(Grid).attrs(() => ({columns: 3}))`
  grid-gap: 0;
`;
